import React, { useEffect, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import type {
  InstallEquipmentKind,
  InstallEquipmentPeriod,
  InstallLaborLine,
  InstallNodeData,
  ProposalNodeSet,
} from '../../types';
import type { ProposalRateSettings } from '../../utils/appSettings';
import {
  INSTALL_EQUIPMENT_KINDS,
  INSTALL_EQUIPMENT_LABELS,
  PERIODS_FOR_KIND,
  equipmentLineTotal,
  isEquipmentKindRequired,
  linesForKind,
  removeEquipmentLine,
  sumKindEquipmentRaw,
  toggleEquipmentKind,
  upsertEquipmentLine,
} from '../../utils/proposalInstallEquipment';
import {
  applyOthersLaborLine,
  emptyInstallLaborLine,
  getInstallLaborLines,
  installLaborPersonLabel,
  isBlankLaborLine,
  isMappedInstallLaborPerson,
  isOthersInstallLaborLine,
  laborLineJobTotal,
  matchInstallLaborPerson,
  withLaborRollup,
  type InstallLaborPerson,
} from '../../utils/proposalInstallLabor';
import { useInstallLaborPeople } from '../../utils/useInstallLaborPeople';
import {
  structureCompareRowProps,
  type StructureLineCompareMap,
} from '../../utils/structureLineCompare';
import './HardwareListTable.css';
import './InstallBreakdownGroups.css';

const ADD_ROLE_VALUE = '__add_role__';
const OTHERS_VALUE = '__others__';

interface InstallBreakdownGroupsProps {
  install: InstallNodeData;
  /** Other structures on this proposal — used to pre-fill 1099 $/day from the last line saved. */
  proposalNodeSets?: Pick<ProposalNodeSet, 'install'>[];
  rates: ProposalRateSettings;
  disabled?: boolean;
  formatCurrency: (value: number) => string;
  onInstallChange: (next: InstallNodeData) => void;
  onDaysChange: (days: number) => void;
  onBlur?: () => void;
  compareFields?: StructureLineCompareMap;
}

const GroupTitle: React.FC<{ label: string; extra?: React.ReactNode }> = ({ label, extra }) => (
  <div className="hardware-group-title">
    <span>{label}</span>
    {extra}
  </div>
);

function applyLaborPerson(line: InstallLaborLine, person: InstallLaborPerson): InstallLaborLine {
  return {
    ...line,
    role: person.name,
    employeeUserId: person.userId,
    ratePerDay: person.ratePerDay != null ? person.ratePerDay : line.ratePerDay,
    quantity: person.userId ? 1 : (line.quantity > 0 ? line.quantity : 1),
  };
}

const LaborPersonField: React.FC<{
  value: string;
  employeeUserId?: string;
  people: InstallLaborPerson[];
  disabled?: boolean;
  placeholder?: string;
  onChangeName: (name: string) => void;
  onPickPerson: (person: InstallLaborPerson) => void;
  onPickOthers: () => void;
  onBlur?: () => void;
}> = ({ value, employeeUserId, people, disabled, placeholder, onChangeName, onPickPerson, onPickOthers, onBlur }) => {
  const roleInputRef = useRef<HTMLInputElement>(null);
  const others = isOthersInstallLaborLine({ role: value, employeeUserId });
  const matched = others ? undefined : matchInstallLaborPerson(people, { userId: employeeUserId, name: value });
  const [addingRole, setAddingRole] = useState(!matched && !others && !!value.trim());

  useEffect(() => {
    if (matched || others) setAddingRole(false);
    else if (value.trim()) setAddingRole(true);
  }, [matched, others, value]);

  return (
    <div className={`install-labor-person${addingRole ? ' install-labor-person--custom' : ''}${others ? ' install-labor-person--others' : ''}`}>
      <select
        value={addingRole ? ADD_ROLE_VALUE : others ? OTHERS_VALUE : (matched?.id || '')}
        disabled={disabled}
        aria-label="Employee"
        onChange={(e) => {
          const next = e.target.value;
          if (next === ADD_ROLE_VALUE) {
            setAddingRole(true);
            requestAnimationFrame(() => roleInputRef.current?.focus());
            return;
          }
          setAddingRole(false);
          if (next === OTHERS_VALUE) {
            onPickOthers();
            return;
          }
          const person = people.find((row) => row.id === next);
          if (person) onPickPerson(person);
        }}
        onBlur={onBlur}
      >
        <option value="">{placeholder}</option>
        {people.map((person) => (
          <option key={person.id} value={person.id}>
            {installLaborPersonLabel(person)}
          </option>
        ))}
        <option value={OTHERS_VALUE}>Others</option>
        <option value={ADD_ROLE_VALUE}>Add role…</option>
      </select>
      {others && (
        <span className="install-labor-others-badge" title="Not mapped to one person — quantity can be more than one">
          Unassigned
        </span>
      )}
      {addingRole && (
        <input
          ref={roleInputRef}
          type="text"
          value={value}
          disabled={disabled}
          placeholder="Role"
          onChange={(e) => onChangeName(e.target.value)}
          onBlur={onBlur}
        />
      )}
    </div>
  );
};

const InstallBreakdownGroups: React.FC<InstallBreakdownGroupsProps> = ({
  install,
  proposalNodeSets,
  rates,
  disabled,
  formatCurrency,
  onInstallChange,
  onDaysChange,
  onBlur,
  compareFields,
}) => {
  const people = useInstallLaborPeople({ proposalNodeSets });
  const [laborDraftId, setLaborDraftId] = useState(() => emptyInstallLaborLine().id);
  const laborRows =
    install.laborLines && install.laborLines.length > 0
      ? install.laborLines
      : getInstallLaborLines(install);
  const last = laborRows[laborRows.length - 1];
  const laborDisplay =
    !disabled && (!last || !isBlankLaborLine(last))
      ? [...laborRows, { ...emptyInstallLaborLine(), id: laborDraftId, quantity: 0 }]
      : laborRows;
  const readonly = disabled ? ' hardware-table--readonly' : '';

  const upsertLabor = (id: string, patch: (row: InstallLaborLine) => InstallLaborLine) => {
    if (laborRows.some((row) => row.id === id)) {
      onInstallChange(withLaborRollup(install, laborRows.map((row) => (row.id === id ? patch(row) : row))));
      return;
    }
    onInstallChange(withLaborRollup(install, [...laborRows, patch({ ...emptyInstallLaborLine(), id })]));
    setLaborDraftId(emptyInstallLaborLine().id);
  };

  return (
    <div className="timber-list-container hardware-list-wrap install-breakdown">
      <div className="hardware-group">
        <GroupTitle
          label="Labor"
          extra={(
            <label
              className={['install-days-field', 'install-days-field--onsite', structureCompareRowProps(compareFields, 'install.days').className].filter(Boolean).join(' ')}
              title={structureCompareRowProps(compareFields, 'install.days').title || 'Working days on the job site, not the full trip. Same as trip length only if there are no weekends off.'}
            >
              On-site days
              <input
                type="number"
                min="0"
                step="1"
                value={install.days || ''}
                disabled={disabled}
                aria-label="On-site days"
                onChange={(e) => onDaysChange(Math.max(0, Math.round(parseFloat(e.target.value) || 0)))}
                onBlur={onBlur}
              />
            </label>
          )}
        />
        <p className="install-days-hint">
          Working days on the job site, not the full trip. Matches trip length only if there are no weekends off.
        </p>
        <table className={`hardware-table install-table--labor${readonly}`}>
          <thead>
            <tr>
              <th className="hardware-col-name">Employee</th>
              <th className="hardware-col-num">People</th>
              <th className="hardware-col-num">$/day</th>
              <th className="hardware-col-num">Total</th>
              <th className="hardware-col-action"></th>
            </tr>
          </thead>
          <tbody>
            {laborDisplay.map((row, index) => {
              const isDraft = row.id === laborDraftId;
              const others = isOthersInstallLaborLine(row);
              const mappedPerson = isMappedInstallLaborPerson(row, people);
              const jobTotal = laborLineJobTotal(row, install.days, people);
              const compare = !isDraft ? structureCompareRowProps(compareFields, row.id) : {};
              return (
                <tr
                  key={row.id || index}
                  className={`${isDraft ? 'hardware-draft-row' : ''} ${others ? 'install-labor-row--others' : ''} ${compare.className || ''}`.trim() || undefined}
                  title={compare.title}
                >
                  <td className="hardware-col-name">
                    <LaborPersonField
                      value={row.role}
                      employeeUserId={row.employeeUserId}
                      people={people}
                      disabled={disabled}
                      placeholder={isDraft ? 'Add Crew' : 'Select employee'}
                      onChangeName={(name) =>
                        upsertLabor(row.id, (line) => {
                          if (name.trim().toLowerCase() === 'others') return applyOthersLaborLine(line);
                          const match = matchInstallLaborPerson(people, { name });
                          return match
                            ? applyLaborPerson(line, match)
                            : { ...line, role: name, employeeUserId: undefined };
                        })
                      }
                      onPickPerson={(person) =>
                        upsertLabor(row.id, (line) => applyLaborPerson(line, person))
                      }
                      onPickOthers={() =>
                        upsertLabor(row.id, (line) => applyOthersLaborLine(line))
                      }
                      onBlur={onBlur}
                    />
                  </td>
                  <td className="hardware-col-num">
                    {others ? (
                      <input
                        type="number"
                        min="0"
                        value={row.quantity || ''}
                        disabled={disabled}
                        aria-label="People in Others"
                        onChange={(e) =>
                          upsertLabor(row.id, (line) => ({ ...line, quantity: parseFloat(e.target.value) || 0 }))
                        }
                        onBlur={onBlur}
                      />
                    ) : (
                      <span className="install-labor-people-dash" aria-label={mappedPerson ? 'One person' : undefined}>—</span>
                    )}
                  </td>
                  <td className="hardware-col-num">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.ratePerDay || ''}
                      disabled={disabled}
                      onChange={(e) =>
                        upsertLabor(row.id, (line) => ({ ...line, ratePerDay: parseFloat(e.target.value) || 0 }))
                      }
                      onBlur={onBlur}
                    />
                  </td>
                  <td className="hardware-col-num">
                    <span>{isDraft && !jobTotal ? '' : formatCurrency(jobTotal)}</span>
                  </td>
                  <td className="hardware-col-action">
                    {!disabled && !isDraft && (
                      <button
                        type="button"
                        className="timber-delete-btn"
                        title="Remove role"
                        onClick={() =>
                          onInstallChange(withLaborRollup(install, laborRows.filter((line) => line.id !== row.id)))
                        }
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="hardware-group">
        <GroupTitle label="Machinery" />
        <table className={`hardware-table install-table--machinery${readonly}`}>
          <thead>
            <tr>
              <th className="hardware-col-name">Name</th>
              <th className="hardware-col-num">Machines</th>
              <th className="hardware-col-mid">Period</th>
              <th className="hardware-col-num">Qty</th>
              <th className="hardware-col-num">Rate</th>
              <th className="hardware-col-num">Total</th>
              <th className="hardware-col-action"></th>
            </tr>
          </thead>
          <tbody>
            {INSTALL_EQUIPMENT_KINDS.map((kind) => (
              <EquipmentKindRows
                key={kind}
                kind={kind}
                install={install}
                rates={rates}
                disabled={disabled}
                formatCurrency={formatCurrency}
                onChange={onInstallChange}
                compareFields={compareFields}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="hardware-group">
        <GroupTitle label="Fuel" />
        <table className={`hardware-table install-table--fuel${readonly}`}>
          <thead>
            <tr>
              <th className="hardware-col-name">Name</th>
              <th className="hardware-col-num">Cost</th>
            </tr>
          </thead>
          <tbody>
            <tr {...structureCompareRowProps(compareFields, 'install.fuel')}>
              <td className="hardware-col-name">
                <span className="hardware-ref-text">Fuel budget</span>
              </td>
              <td className="hardware-col-num">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={install.fuelBudget || ''}
                  disabled={disabled}
                  onChange={(e) =>
                    onInstallChange({ ...install, fuelBudget: parseFloat(e.target.value) || 0 })
                  }
                  onBlur={onBlur}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

function EquipmentKindRows({
  kind,
  install,
  rates,
  disabled,
  formatCurrency,
  onChange,
  compareFields,
}: {
  kind: InstallEquipmentKind;
  install: InstallNodeData;
  rates: ProposalRateSettings;
  disabled?: boolean;
  formatCurrency: (value: number) => string;
  onChange: (next: InstallNodeData) => void;
  compareFields?: StructureLineCompareMap;
}) {
  const on = isEquipmentKindRequired(install, kind);
  const lines = linesForKind(install, kind);
  const subtotal = sumKindEquipmentRaw(install, kind, rates);
  const label = INSTALL_EQUIPMENT_LABELS[kind];

  const nameCell = (
    <td className="hardware-col-name">
      <label className="install-kind-label">
        <input
          type="checkbox"
          checked={on}
          disabled={disabled}
          onChange={(e) => onChange(toggleEquipmentKind(install, kind, e.target.checked, rates))}
        />
        {label}
      </label>
    </td>
  );

  const kindCompare = structureCompareRowProps(compareFields, `install.equip.${kind}`);
  if (!on) {
    return (
      <tr className={kindCompare.className} title={kindCompare.title}>
        {nameCell}
        <td className="hardware-col-num" />
        <td className="hardware-col-mid" />
        <td className="hardware-col-num" />
        <td className="hardware-col-num" />
        <td className="hardware-col-num" />
        <td className="hardware-col-num" />
        <td className="hardware-col-action" />
      </tr>
    );
  }

  if (lines.length === 0) {
    return (
      <tr className={kindCompare.className} title={kindCompare.title}>
        {nameCell}
        <td className="hardware-col-num" />
        <td className="hardware-col-mid" />
        <td className="hardware-col-num" />
        <td className="hardware-col-num" />
        <td className="hardware-col-num">
          <span>{formatCurrency(subtotal)}</span>
        </td>
        <td className="hardware-col-action" />
      </tr>
    );
  }

  return (
    <>
      {lines.map((line, index) => {
        const lineCompare = structureCompareRowProps(compareFields, line.id);
        const rowCompare = index === 0 && kindCompare.className ? kindCompare : lineCompare;
        return (
        <tr
          key={line.id}
          className={rowCompare.className}
          title={rowCompare.title || lineCompare.title}
        >
          {index === 0 ? nameCell : <td className="hardware-col-name" />}
          <td className="hardware-col-num">
            <input
              type="number"
              min="0"
              step="1"
              value={line.units ?? 1}
              disabled={disabled}
              aria-label={`${label} machines`}
              title="How many of this machine"
              onChange={(e) =>
                onChange(
                  upsertEquipmentLine(install, line.id, {
                    units: Math.max(0, parseFloat(e.target.value) || 0),
                  }),
                )
              }
            />
          </td>
          <td className="hardware-col-mid">
            <select
              value={line.overtime ? 'overtime' : line.period}
              disabled={disabled}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'overtime') {
                  onChange(upsertEquipmentLine(install, line.id, { overtime: true, period: 'hour' }));
                  return;
                }
                onChange(
                  upsertEquipmentLine(install, line.id, {
                    period: value as InstallEquipmentPeriod,
                    overtime: false,
                  }),
                );
              }}
            >
              {PERIODS_FOR_KIND[kind].map((period) => (
                <option key={period} value={period}>
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </option>
              ))}
              {kind === 'crane' && <option value="overtime">Overtime hour</option>}
            </select>
          </td>
          <td className="hardware-col-num">
            <input
              type="number"
              min="0"
              step="0.5"
              value={line.quantity}
              disabled={disabled}
              aria-label={`${label} ${line.period} quantity`}
              title={`How many ${line.overtime ? 'overtime hours' : `${line.period}s`}`}
              onChange={(e) =>
                onChange(
                  upsertEquipmentLine(install, line.id, {
                    quantity: parseFloat(e.target.value) || 0,
                  }),
                )
              }
            />
          </td>
          <td className="hardware-col-num">
            <input
              type="number"
              min="0"
              step="0.01"
              value={line.rate}
              disabled={disabled}
              aria-label={`${label} rate`}
              onChange={(e) =>
                onChange(
                  upsertEquipmentLine(install, line.id, {
                    rate: parseFloat(e.target.value) || 0,
                  }),
                )
              }
            />
          </td>
          <td className="hardware-col-num">
            <span>{formatCurrency(equipmentLineTotal(line))}</span>
          </td>
          <td className="hardware-col-action">
            {!disabled && lines.length > 1 && (
              <button
                type="button"
                className="timber-delete-btn"
                title="Remove period"
                onClick={() => onChange(removeEquipmentLine(install, line.id))}
              >
                <Trash2 size={12} />
              </button>
            )}
          </td>
        </tr>
        );
      })}
    </>
  );
}

export default InstallBreakdownGroups;
